import * as THREE from 'three';
import { box, weld } from '../level/VoxelFactory.js';
import { RAILCAR, FLATCAR, COLORS } from '../core/Constants.js';

/**
 * Geometry is built once per colour and shared by every car of that colour.
 * Cars are laid out along +x with the headlight on the +x end; a consist Group
 * is rotated 180° when it travels the other way.
 */

function wheelset(parts, length, width, y) {
  const inset = length / 2 - 0.9;
  for (const x of [-inset, inset]) {
    for (const dz of [-1, 1]) {
      parts.push(box(1.5, 0.52, 0.42, COLORS.WHEEL, x, y, dz * (width / 2 - 0.18)));
    }
  }
}

export function buildRailcarGeometry(bodyColor, withHeadlight) {
  const { LENGTH: L, WIDTH: W, BODY_H: H, FLOOR_Y: F } = RAILCAR;
  const parts = [];

  wheelset(parts, L, W, 0.26);
  parts.push(box(L - 0.4, 0.42, W - 0.5, COLORS.CHASSIS, 0, F - 0.16, 0));
  parts.push(box(L, H, W, bodyColor, 0, F + H / 2, 0));

  // The camera looks down on these, so the roof is the face you actually read:
  // keep it the body colour and let only thin cant rails and a vent strip be
  // light, otherwise every train turns into an anonymous grey slab.
  parts.push(box(L - 0.6, 0.16, W - 0.04, bodyColor, 0, F + H + 0.08, 0));
  for (const dz of [-1, 1]) {
    parts.push(box(L - 0.6, 0.18, 0.2, COLORS.TRAIN_ROOF, 0, F + H + 0.09, dz * (W / 2 - 0.12)));
  }
  parts.push(box(L - 2.4, 0.2, 0.26, COLORS.TRAIN_ROOF, 0, F + H + 0.1, 0));
  parts.push(box(L, 0.16, W + 0.04, COLORS.TRAIN_SKIRT, 0, F + 0.08, 0));

  // Window band — five lit panes per side, punched through as inset blocks.
  const paneCount = 5;
  const span = L - 1.9;
  for (let i = 0; i < paneCount; i++) {
    const t = paneCount === 1 ? 0.5 : i / (paneCount - 1);
    const x = -span / 2 + t * span;
    parts.push(box(0.72, 0.5, W + 0.06, COLORS.WINDOW, x, F + H * 0.6, 0));
  }

  if (withHeadlight) {
    parts.push(box(0.3, 0.3, 0.4, COLORS.HEADLIGHT, L / 2 + 0.06, F + 0.48, 0));
    parts.push(box(0.44, H * 0.46, W - 0.5, COLORS.TRAIN_SKIRT, L / 2 - 0.04, F + H * 0.23, 0));
  }

  return weld(parts);
}

export function buildFlatcarGeometry() {
  const { LENGTH: L, WIDTH: W, DECK_Y: D, WALL_H: WH } = FLATCAR;
  const parts = [];

  wheelset(parts, L, W, 0.26);
  parts.push(box(L - 0.5, 0.36, W - 0.6, COLORS.CHASSIS, 0, 0.58, 0));

  // Dark deck with a bright yellow kerb all the way round: from above it reads
  // as a marked-out platform you are meant to stand on.
  parts.push(box(L, 0.22, W, COLORS.FLATCAR_TRIM, 0, D - 0.11, 0));
  parts.push(box(L - 0.7, 0.24, W - 0.7, COLORS.FLATCAR_DECK, 0, D - 0.08, 0));

  for (const dz of [-1, 1]) {
    parts.push(box(L, WH, 0.26, COLORS.FLATCAR, 0, D + WH / 2, dz * (W / 2 - 0.13)));
  }
  for (const dx of [-1, 1]) {
    parts.push(box(0.26, WH, W - 0.5, COLORS.FLATCAR, dx * (L / 2 - 0.13), D + WH / 2, 0));
  }

  // Hazard blocks along the sides — the visual cue that this one is safe.
  const stripes = 4;
  for (let i = 0; i < stripes; i++) {
    const x = -L / 2 + 1.3 + (i * (L - 2.6)) / (stripes - 1);
    for (const dz of [-1, 1]) {
      parts.push(
        box(0.55, WH * 0.72, 0.3, COLORS.FLATCAR_TRIM, x, D + WH / 2, dz * (W / 2 - 0.13)),
      );
    }
  }

  return weld(parts);
}

/**
 * A consist: one or more cars coupled together, wrapping around the field.
 * `x` is the centre of the whole consist in world space.
 */
export class Consist {
  constructor(geometryFor, material, carCount, isFreight, dir) {
    this.isFreight = isFreight;
    this.dir = dir;
    this.group = new THREE.Group();
    this.group.rotation.y = dir > 0 ? 0 : Math.PI;

    const spec = isFreight ? FLATCAR : RAILCAR;
    const pitch = spec.LENGTH + spec.GAP;
    this.length = carCount * spec.LENGTH + (carCount - 1) * spec.GAP;
    this.halfLength = this.length / 2;

    for (let i = 0; i < carCount; i++) {
      // Car 0 sits at the +x (leading) end and carries the headlight.
      const geo = geometryFor(i === 0);
      const mesh = new THREE.Mesh(geo, material);
      mesh.position.x = this.halfLength - spec.LENGTH / 2 - i * pitch;
      mesh.frustumCulled = false;
      this.group.add(mesh);
    }

    this.x = 0;
    this.derailed = false;
    this.vx = 0;
    this.vy = 0;
    this.vz = 0;
    this.worldY = 0;
    this.worldZ = 0;
    this.rotX = 0;
    this.rotY = dir > 0 ? 0 : Math.PI;
    this.rotZ = 0;
    this.vRotX = 0;
    this.vRotY = 0;
    this.vRotZ = 0;
  }

  setX(x) {
    if (this.derailed) return;
    this.x = x;
    this.group.position.x = x;
  }

  derail(scene, lane, impulseVx, impulseVy, impulseVz) {
    if (this.derailed) return;
    this.derailed = true;
    this.lane = lane;
    this.derailTimer = 0;
    this.worldZ = lane ? lane.z : 0;
    this.worldY = this.isFreight ? 0.92 : 0.0;
    this.vx = impulseVx;
    this.vy = impulseVy;
    this.vz = impulseVz;
    this.vRotX = (Math.random() - 0.5) * 5.0;
    this.vRotY = (Math.random() - 0.5) * 3.0;
    this.vRotZ = (Math.random() - 0.5) * 6.0;

    // Reparent group to main scene for world space positioning across tracks
    const worldPos = new THREE.Vector3();
    this.group.getWorldPosition(worldPos);
    scene.add(this.group);
    this.x = worldPos.x;
    this.worldY = worldPos.y;
    this.worldZ = worldPos.z;
    this.group.position.set(this.x, this.worldY, this.worldZ);
  }

  restoreService() {
    this.derailed = false;
    this.derailTimer = 0;
    this.group.scale.set(1, 1, 1);
    this.group.rotation.set(0, this.dir > 0 ? 0 : Math.PI, 0);
    this.group.position.set(this.x, 0, 0);
    if (this.lane && this.lane.group) {
      this.lane.group.add(this.group);
    }
    this.group.visible = true;
  }

  updatePhysics(dt) {
    if (!this.derailed) return;

    const groundY = 0.0;
    this.vy -= 26.0 * dt;

    this.x += this.vx * dt;
    this.worldY += this.vy * dt;
    this.worldZ += this.vz * dt;

    this.rotX += this.vRotX * dt;
    this.rotY += this.vRotY * dt;
    this.rotZ += this.vRotZ * dt;

    if (this.worldY <= groundY) {
      this.worldY = groundY;
      if (Math.abs(this.vy) > 1.2) {
        this.vy = -this.vy * 0.28;
      } else {
        this.vy = 0;
      }
      this.vx *= Math.max(0, 1 - 2.2 * dt);
      this.vz *= Math.max(0, 1 - 2.2 * dt);
      this.vRotX *= Math.max(0, 1 - 3.5 * dt);
      this.vRotY *= Math.max(0, 1 - 3.5 * dt);
      this.vRotZ *= Math.max(0, 1 - 3.5 * dt);

      // Tilt slightly on track bed
      const targetTilt = this.vz > 0 ? 0.3 : -0.3;
      this.rotZ = THREE.MathUtils.lerp(this.rotZ, targetTilt, dt * 4);
    }

    this.group.position.set(this.x, this.worldY, this.worldZ);
    this.group.rotation.set(this.rotX, this.rotY, this.rotZ);
  }

  /** World-space x extents, used for collision and for riding. */
  get min() { return this.x - this.halfLength; }
  get max() { return this.x + this.halfLength; }

  contains(x, pad = 0) {
    return x > this.min + pad && x < this.max - pad;
  }
}
